/**
 * Ratings Screen
 *
 * Listagem e gerenciamento de avaliações
 */
import React, { useState } from 'react'
import {
	Trash2,
	Eye,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import FilterBar from '../molecules/FilterBar'
import { Modal, ConfirmModal } from '../molecules/Modal'
import Avatar from '../atoms/Avatar'
import Card from '../atoms/Card'
import { LoadingScreen } from '../atoms/Spinner'
import { ratingsApi } from '../../core/admin.api'
import { usePaginatedList } from '../../core/admin.hooks'
import { formatDate } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function RatingsScreen() {
	const [filters, setFilters] = useState({})
	const [selectedRating, setSelectedRating] = useState(null)
	const [showDetailModal, setShowDetailModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		setSearch,
		refetch,
	} = usePaginatedList(ratingsApi.list, filters)

	const ratings = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Filtros disponíveis
	const filterOptions = [
		{
			key: 'minRating',
			label: 'Nota mínima',
			type: 'select',
			options: [
				{ value: '', label: 'Todas' },
				{ value: '3', label: '3+' },
				{ value: '5', label: '5+' },
				{ value: '7', label: '7+' },
				{ value: '9', label: '9+' },
			],
		},
	]

	// Renderizar nota (escala 1-10)
	const renderRating = (rating) => {
		const getColor = (value) => {
			if (value >= 8) return 'text-success bg-success/10'
			if (value >= 5) return 'text-warning bg-warning/10'
			return 'text-error bg-error/10'
		}

		return (
			<div className="flex items-center gap-2">
				<span className={`px-2 py-1 rounded-md font-bold ${getColor(rating)}`}>
					{rating}/10
				</span>
			</div>
		)
	}

	// Colunas da tabela
	const columns = [
		{
			key: 'rating',
			label: 'Nota',
			render: (row) => renderRating(row.rating),
		},
		{
			key: 'from',
			label: 'Avaliador',
			render: (row) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={row.reviewer?.avatar}
						name={row.reviewer?.name}
						size="sm"
					/>
					<span className="text-text-body">
						{row.reviewer?.name} {row.reviewer?.lastname || ''}
					</span>
				</div>
			),
		},
		{
			key: 'to',
			label: 'Avaliado',
			render: (row) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={row.reviewee?.avatar}
						name={row.reviewee?.name}
						size="sm"
					/>
					<span className="text-text-body">
						{row.reviewee?.name} {row.reviewee?.lastname || ''}
					</span>
				</div>
			),
		},
		{
			key: 'match',
			label: 'Partida',
			render: (row) => (
				<span className="text-text-body text-sm">
					{row.match?.club?.name || 'N/A'}
				</span>
			),
		},
		{
			key: 'createdAt',
			label: 'Data',
			render: (row) => (
				<span className="text-text-body text-sm">
					{formatDate(row.createdAt, 'dd/MM/yyyy')}
				</span>
			),
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Eye,
			label: 'Ver detalhes',
			onClick: () => {
				setSelectedRating(row)
				setShowDetailModal(true)
			},
		},
		{
			icon: Trash2,
			label: 'Excluir',
			onClick: () => {
				setSelectedRating(row)
				setShowDeleteModal(true)
			},
			variant: 'danger',
		},
	]

	// Handler para excluir
	const handleDelete = async () => {
		if (!selectedRating) return

		setActionLoading(true)
		try {
			await ratingsApi.delete(selectedRating._id)
			toast.success('Avaliação excluída com sucesso')
			setShowDeleteModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir avaliação')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Avaliações" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Avaliações"
				subtitle={`${total} avaliações registradas`}
			/>

			<div className="p-3 md:p-6 space-y-3 md:space-y-4">
				{/* Filtros */}
				<FilterBar
					filters={filterOptions}
					values={filters}
					onChange={setFilters}
					onSearch={setSearch}
					searchPlaceholder="Buscar por usuário..."
				/>

				{/* Tabela */}
				<DataTable
					columns={columns}
					data={ratings}
					loading={loading}
					emptyMessage="Nenhuma avaliação encontrada"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
				/>
			</div>

			{/* Modal de detalhes */}
			<Modal
				isOpen={showDetailModal}
				onClose={() => setShowDetailModal(false)}
				title="Detalhes da Avaliação"
			>
				{selectedRating && (
					<div className="space-y-4">
						{/* Nota */}
						<div className="text-center py-3 md:py-4">
							{renderRating(selectedRating.rating)}
						</div>

						{/* De / Para */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
							<div>
								<p className="text-xs md:text-sm text-text-body mb-2">Avaliador</p>
								<div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-base-400 rounded-lg">
									<Avatar
										src={selectedRating.reviewer?.avatar}
										name={selectedRating.reviewer?.name}
										size="sm"
									/>
									<div className="min-w-0">
										<p className="font-medium text-sm truncate">{selectedRating.reviewer?.name} {selectedRating.reviewer?.lastname || ''}</p>
									</div>
								</div>
							</div>
							<div>
								<p className="text-xs md:text-sm text-text-body mb-2">Avaliado</p>
								<div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-base-400 rounded-lg">
									<Avatar
										src={selectedRating.reviewee?.avatar}
										name={selectedRating.reviewee?.name}
										size="sm"
									/>
									<div className="min-w-0">
										<p className="font-medium text-sm truncate">{selectedRating.reviewee?.name} {selectedRating.reviewee?.lastname || ''}</p>
									</div>
								</div>
							</div>
						</div>

						{/* Comentário */}
						{selectedRating.comment && (
							<div>
								<p className="text-xs md:text-sm text-text-body mb-1">Comentário</p>
								<Card className="p-3 md:p-4 bg-base-400">
									<p className="text-text text-sm whitespace-pre-wrap">
										{selectedRating.comment}
									</p>
								</Card>
							</div>
						)}

						{/* Data */}
						<div className="text-center text-xs md:text-sm text-text-body pt-3 md:pt-4 border-t border-base-200">
							{formatDate(selectedRating.createdAt, "dd/MM/yyyy 'às' HH:mm")}
						</div>
					</div>
				)}
			</Modal>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Avaliação"
				message="Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita e pode afetar a média do usuário avaliado."
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}
